import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { FeedbackProvider } from './components/feedback/Feedback';
import ErrorBoundary from './components/feedback/ErrorBoundary';
import { PageLoader } from './components/feedback/PageLoader';
import './App.css';

// Marketing + auth pages load eagerly (small, first paint). Everything else is
// code-split with React.lazy so e.g. the public e-sign page never downloads
// three.js / mapbox / konva from the editors and map pages.

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import BookCallPage from './pages/BookCallPage';
import AlexBookCallPage from './pages/AlexBookCallPage';
import PrivacyPage from './pages/PrivacyPage';
import WhyWeWin from './pages/WhyWeWin';
import PricingPage from './pages/PricingPage';
import TermsPage from './pages/TermsPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import WorkspaceSelectorPage from './pages/WorkspaceSelectorPage';
const TeamManagementPage = lazy(() => import('./pages/TeamManagementPage'));
const GetSetUpPage = lazy(() => import('./pages/GetSetUpPage'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const ReviewJobs = lazy(() => import('./pages/ReviewJobs'));
const ReviewStudio = lazy(() => import('./pages/ReviewStudio'));
const VexcelReviewStudio = lazy(() => import('./pages/VexcelReviewStudio'));
const MeasurementReviewPage = lazy(() => import('./pages/measurementReview/MeasurementReviewEditor'));
const EmbeddedReviewPage = lazy(() => import('./pages/measurementReview/EmbeddedReviewPage'));
const ReviewerDashboard = lazy(() => import('./pages/ReviewerDashboard'));
const MyReports = lazy(() => import('./pages/MyReports'));
const ApiDocs = lazy(() => import('./pages/ApiDocs'));
const ReviewerApplyPage = lazy(() => import('./pages/ReviewerApplyPage'));
const ReviewerTestAdmin = lazy(() => import('./pages/ReviewerTestAdmin'));
const PricingAdmin = lazy(() => import('./pages/admin/PricingAdmin'));

// Notify Network (homeowner portal — no auth required at router level; pages handle own auth)
const ActivatePage = lazy(() => import('./pages/notify/ActivatePage'));
const VerifyPage = lazy(() => import('./pages/notify/VerifyPage'));
const NotifyHomePage = lazy(() => import('./pages/notify/NotifyHomePage'));
const PreferencesPage = lazy(() => import('./pages/notify/PreferencesPage'));
const RequestServicePage = lazy(() => import('./pages/notify/RequestServicePage'));

// Notify Network — Public website (for 10DLC registration / homeowner education)
const NotifyNetworkLanding = lazy(() => import('./pages/notify-network/NotifyNetworkLanding'));
const PrivacyPolicyPage = lazy(() => import('./pages/notify-network/PrivacyPolicyPage'));
const SMSTermsPage = lazy(() => import('./pages/notify-network/SMSTermsPage'));
const OptInDemoPage = lazy(() => import('./pages/notify-network/OptInDemoPage'));

// FinancialOS
const FinancialOSLayout = lazy(() => import('./components/FinancialOSLayout'));
const FinancialOSDashboard = lazy(() => import('./pages/financialos/FinancialOSDashboard'));
const Jobs = lazy(() => import('./pages/financialos/Jobs'));
const JobPortal = lazy(() => import('./pages/financialos/JobPortal'));
const Businesses = lazy(() => import('./pages/financialos/Businesses'));
const Estimates = lazy(() => import('./pages/financialos/Estimates'));
const EstimateBuilder = lazy(() => import('./pages/financialos/EstimateBuilder'));
const EstimateBuilderV2 = lazy(() => import('./pages/financialos/EstimateBuilderV2'));
const Pricebooks = lazy(() => import('./pages/financialos/Pricebooks'));
const Invoices = lazy(() => import('./pages/financialos/Invoices'));
const InvoiceBuilder = lazy(() => import('./pages/financialos/InvoiceBuilder'));
const ReportTemplatesList = lazy(() => import('./pages/financialos/reports/ReportTemplatesList'));
const TemplateBuilder = lazy(() => import('./pages/financialos/reports/TemplateBuilder'));
const ClientSigningPortal = lazy(() => import('./pages/financialos/reports/ClientSigningPortal'));
const SignerPortal = lazy(() => import('./pages/financialos/reports/SignerPortal'));
const AuditLog = lazy(() => import('./pages/financialos/AuditLog'));
const ComingSoon = lazy(() => import('./pages/financialos/ComingSoon'));
const PublicGallery = lazy(() => import('./pages/PublicGallery'));
const SaasCalculator = lazy(() => import('./pages/SaasCalculator'));

// Payment integrations
const GHLMarketplaceInstall = lazy(() => import('./pages/integrations/GHLMarketplaceInstall'));
const MXSetup = lazy(() => import('./pages/payments/MXSetup'));
const TransactionDashboard = lazy(() => import('./pages/payments/TransactionDashboard'));

// StormTriggered Ads Module Pages
const StormAdsDashboard = lazy(() => import('./pages/storm-ads/StormAdsDashboard'));
const StormEvents = lazy(() => import('./pages/storm-ads/StormEvents'));
const StormCampaigns = lazy(() => import('./pages/storm-ads/StormCampaigns'));
const StormTemplates = lazy(() => import('./pages/storm-ads/StormTemplates'));
const StormSettings = lazy(() => import('./pages/storm-ads/StormSettings'));

// Dev-only review editor harness (fixture data, no sign-in). The production
// build drops this branch entirely (NODE_ENV is inlined), so the route and
// its chunk never ship.
const ReviewHarnessPage = process.env.NODE_ENV !== 'production'
  ? React.lazy(() => import('./pages/measurementReview/dev/ReviewHarnessPage'))
  : null;

// Role-aware index for /financialos:
// Sales reps land on the Job Board; owners/managers see the KPI Dashboard.
function FinancialOSIndex() {
  const orgRole = localStorage.getItem('ra_org_role');
  const roleOverride = localStorage.getItem('ra_role_override');
  const effectiveRole = roleOverride || orgRole;
  if (effectiveRole === 'sales') {
    return <Navigate to="/financialos/jobs" replace />;
  }
  return <FinancialOSDashboard />;
}

/**
 * SupabaseCodeCatcher
 * Supabase sometimes delivers the magic link to the Site URL (homepage) instead
 * of /auth/callback when the emailRedirectTo URL isn't in the allowlist yet.
 * This component runs on every route and immediately forwards any
 * ?code= (PKCE) or #access_token= (implicit) params to /auth/callback
 * so AuthCallbackPage can process them.
 */
function SupabaseCodeCatcher() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only intercept if we're NOT already on /auth/callback
    if (location.pathname === '/auth/callback') return;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const hash = location.hash;
    const hasAccessToken = hash.includes('access_token=');
    const hasTokenHash = params.get('token_hash');

    if (code || hasAccessToken || hasTokenHash) {
      console.log('[SupabaseCodeCatcher] Intercepted Supabase auth params on', location.pathname, '→ /auth/callback');
      // Forward to /auth/callback preserving all query params and hash
      navigate('/auth/callback' + location.search + location.hash, { replace: true });
    }
  }, [location, navigate]);

  return null;
}


// Resets the boundary on navigation so a crash on one page doesn't stick.
function RouteErrorBoundary({ children }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>;
}

function App() {
  return (
    <Router>
      <FeedbackProvider>
      <div className="App">
        <SupabaseCodeCatcher />
        <RouteErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/why-we-win" element={<WhyWeWin />} />
          <Route path="/packages" element={<PricingPage />} />
          <Route path="/pricing" element={<Navigate to="/packages" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/book" element={<BookCallPage />} />
          <Route path="/alex" element={<AlexBookCallPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          {/* Magic Link Auth Flow */}
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/workspace-select" element={<WorkspaceSelectorPage />} />

          {/* App Routes */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/generator" element={<Dashboard />} />
          <Route path="/weather-watch" element={<Dashboard />} />
          <Route path="/weather-watch/storm-swaths" element={<Dashboard />} />
          <Route path="/prospecting" element={<Dashboard />} />
          <Route path="/roof-measure" element={<Dashboard />} />
          <Route path="/measure-review/:runId" element={<MeasurementReviewPage />} />
          <Route path="/embed/review" element={<EmbeddedReviewPage />} />
          {ReviewHarnessPage && (
            <Route path="/dev/review-harness" element={<ReviewHarnessPage />} />
          )}
          <Route path="/my-reports" element={<MyReports />} />

          {/* Settings & Backoffice Integrations */}
          <Route path="/settings/team" element={<TeamManagementPage />} />
          <Route path="/get-set-up" element={<GetSetUpPage />} />
          <Route path="/dashboard/settings/payments" element={<MXSetup />} />
          <Route path="/dashboard/settings/payments/boarding" element={<MXSetup />} /> {/* Reused for now */}
          <Route path="/integrations/ghl/install" element={<GHLMarketplaceInstall />} />

          {/* Internal Reviewer Routes */}
          <Route path="/review/jobs" element={<ReviewJobs />} />
          <Route path="/review/jobs/:jobId/studio" element={<ReviewStudio />} />
          <Route path="/professional-review/:reviewId" element={<VexcelReviewStudio />} />
          <Route path="/reviewer/dashboard" element={<ReviewerDashboard />} />

          {/* Docs */}
          <Route path="/api-docs" element={<ApiDocs />} />

          {/* Reviewer Hiring Test — public */}
          <Route path="/reviewer-apply" element={<ReviewerApplyPage />} />
          {/* Reviewer Hiring Admin — admin only */}
          <Route path="/admin/reviewer-test" element={<ReviewerTestAdmin />} />
          <Route path="/admin/pricing" element={<PricingAdmin />} />

          {/* Notify Network — homeowner portal */}
          <Route path="/notify/activate/:ref" element={<ActivatePage />} />
          <Route path="/notify/verify" element={<VerifyPage />} />
          <Route path="/notify/home" element={<NotifyHomePage />} />
          <Route path="/notify/preferences" element={<PreferencesPage />} />
          <Route path="/notify/request" element={<RequestServicePage />} />

          {/* Notify Network — Public website (10DLC / homeowner education) */}
          <Route path="/notify-network" element={<NotifyNetworkLanding />} />
          <Route path="/notify-network/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/notify-network/terms" element={<SMSTermsPage />} />
          <Route path="/notify-network/optin-demo" element={<OptInDemoPage />} />

          {/* Standalone Tools */}
          <Route path="/tools/saas-calculator" element={<SaasCalculator />} />
          <Route path="/public/sign/t/:token" element={<SignerPortal />} />
          <Route path="/public/sign/:reportId" element={<ClientSigningPortal />} />
          <Route path="/public/gallery/:token" element={<PublicGallery />} />

          {/* StormTriggered Ads Module Routes */}
          <Route path="/storm-ads" element={<StormAdsDashboard />} />
          <Route path="/storm-ads/events" element={<StormEvents />} />
          <Route path="/storm-ads/campaigns" element={<StormCampaigns />} />
          <Route path="/storm-ads/templates" element={<StormTemplates />} />
          <Route path="/storm-ads/settings" element={<StormSettings />} />

          {/* FinancialOS — nested under layout shell with persistent sidebar */}
          <Route path="/financialos" element={<FinancialOSLayout />}>
            <Route index element={<FinancialOSIndex />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="businesses" element={<Businesses />} />
            <Route path="jobs/:id" element={<JobPortal />} />
            <Route path="estimates" element={<Estimates />} />
            <Route path="estimates/:id" element={<EstimateBuilder />} />
            <Route path="estimates/v2/:id" element={<EstimateBuilderV2 />} />
            <Route path="pricebooks" element={<Pricebooks />} />
            <Route path="invoices" element={<Invoices />} />
            <Route path="invoices/:id" element={<InvoiceBuilder />} />
            <Route path="payments" element={<TransactionDashboard />} />
            <Route path="reports/pnl" element={<FinancialOSDashboard />} />
            {/* Not built yet — show a clear placeholder instead of an unrelated page */}
            <Route path="bills" element={<ComingSoon title="Bills" />} />
            <Route path="chart-of-accounts" element={<ComingSoon title="Chart of Accounts" />} />
            <Route path="journal" element={<ComingSoon title="Journal" />} />
            <Route path="commissions" element={<ComingSoon title="Commissions" />} />
            <Route path="reports/profitability" element={<ComingSoon title="Job Profitability Report" />} />
            <Route path="reports/ar-aging" element={<ComingSoon title="A/R Aging Report" />} />
            <Route path="reports/templates" element={<ReportTemplatesList />} />
            <Route path="reports/builder/:id" element={<TemplateBuilder />} />
            <Route path="audit-log" element={<AuditLog />} />
          </Route>
        </Routes>
        </Suspense>
        </RouteErrorBoundary>
      </div>
      </FeedbackProvider>
    </Router>
  );
}

export default App;
